import { ContentLabel } from "@workshop/types";
import { GeneratedSkedApiClient } from "./generated-client";
import type { paths } from "./generated/sked-api-openapi-types";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();

type GetLabelsOperation = paths["/labels"]["get"];
type CreateLabelOperation = paths["/labels"]["post"];
type UpdateLabelOperation = paths["/labels/{labelId}"]["put"];
type DeleteLabelOperation = paths["/labels/{labelId}"]["delete"];

type LabelResponse =
  GetLabelsOperation["responses"][200]["content"]["application/json"][number];
type CreateLabelBody =
  CreateLabelOperation["requestBody"]["content"]["application/json"];
type CreateLabelResponse =
  CreateLabelOperation["responses"][200]["content"]["application/json"];
type UpdateLabelBody =
  UpdateLabelOperation["requestBody"]["content"]["application/json"];
type UpdateLabelResponse =
  UpdateLabelOperation["responses"][200]["content"]["application/json"];

export async function getContentLabels(
  groupId: string
): Promise<{ data: ContentLabel[] }> {
  const result = await generatedSkedApiClient.client.GET("/labels", {
    params: {
      query: { groupId },
    },
  });

  return {
    data: (await generatedSkedApiClient.unwrap(
      result
    )) as LabelResponse[] as ContentLabel[],
  };
}
export async function getAllContentLabels(): Promise<{ data: ContentLabel[] }> {
  const result = await generatedSkedApiClient.client.GET("/labels");

  return {
    data: (await generatedSkedApiClient.unwrap(
      result
    )) as LabelResponse[] as ContentLabel[],
  };
}

export async function createContentLabel(
  label: ContentLabel
): Promise<{ data: ContentLabel }> {
  const result = await generatedSkedApiClient.client.POST("/labels", {
    body: toCreateLabelBody(label),
  });

  return {
    data: (await generatedSkedApiClient.unwrap(
      result
    )) as CreateLabelResponse as ContentLabel,
  };
}

export async function updateContentLabel(
  labelId: string,
  label: Partial<ContentLabel>
): Promise<{ data: ContentLabel }> {
  const result = await generatedSkedApiClient.client.PUT("/labels/{labelId}", {
    params: {
      path: {
        labelId,
      },
    },
    body: toUpdateLabelBody(label),
  });

  return {
    data: (await generatedSkedApiClient.unwrap(
      result
    )) as UpdateLabelResponse as ContentLabel,
  };
}

export async function removeContentLabel(
  labelId: string
): Promise<{ data: ContentLabel | undefined }> {
  const result = await generatedSkedApiClient.client.DELETE(
    "/labels/{labelId}",
    {
      params: {
        path: {
          labelId,
        },
      },
    }
  );

  if (!result.response.ok) {
    await generatedSkedApiClient.unwrap(result);
  }

  return {
    data: result.data as DeleteLabelOperation["responses"][204]["content"],
  };
}

function toCreateLabelBody(label: ContentLabel): CreateLabelBody {
  return {
    groupId: label.groupId,
    groupName: label.groupName,
    name: label.name,
    type: label.type || "CONTENT_PILLAR",
    description: label.description,
    emoji: label.emoji,
    color: label.color,
    generatedTopic: label.generatedTopic,
  };
}

function toUpdateLabelBody(label: Partial<ContentLabel>): UpdateLabelBody {
  return {
    name: label.name,
    type: label.type,
    status: label.status === "DELETED" ? undefined : label.status,
    description: label.description,
    emoji: label.emoji,
    color: label.color,
    generatedTopic: label.generatedTopic,
  };
}
